import { ChangeEventHandler, useState } from 'react'

import { isEqual } from 'lodash-es'
import { useTranslation } from 'react-i18next'
import { SingleValue } from 'react-select'
import { connect, DataItem, navigateTo, useDataItem } from 'scrivito'

import { TicketSublistItem } from './TicketSublistItem'
import { useTicketTabData } from './useTicketTabData'
import { LIST_SIZE } from '../../const'
import { StringText } from '@/Atomic/Atoms/StringText'
import { Search } from '@/Atomic/Molecules/Inputs/Search'
import { Option } from '@/Atomic/Molecules/Inputs/Select/types'
import { SimpleSelect } from '@/Atomic/Molecules/SimpleSelect'
import { ScrollablePanel } from '@/Atomic/Templates/ScrollablePanel'
import { Sublist, SublistFooter } from '@/Atomic/Templates/Sublist'
import { DataClassActivityDrawer } from '@/Atomic/Widgets/DataClassDrawer/DataClass.ActivityDrawer'
import { DataItemDrawer } from '@/Atomic/Widgets/DataItemDrawer'
import { showInfoBoxToast } from '@/Atomic/Widgets/Toasts/InfoBoxToast'
import { errorToast } from '@/Components/Toast'
import { ActivityProjectRelation } from '@/Data/ActivityProjectRelation/ActivityProjectRelationDataClass'
import {
  DataClassName,
  PROJECT_ASC_ORDER,
  PROJECT_DESC_ORDER,
} from '@/Data/const'
import { DataScopeOrder } from '@/Data/types'
import { useModalHandler } from '@/hooks/useModalHandler'
import { useDataItemDrawer } from '@/utils/CRUD/hooks/useDataItemDrawer'
import { getDataItemTitle } from '@/utils/dataClassMap'
import { getEntityLabelKey } from '@/utils/getEntityLabelKey'
import { reportError } from '@/utils/reportError'

import './TicketSublistItem.scss'

interface TicketTabProps {
  searchInputName: string
}

export const TicketTab: React.FC<TicketTabProps> = connect(
  ({ searchInputName }) => {
    const { t } = useTranslation()
    const dataItem = useDataItem()
    const subjectId = dataItem?.id() ?? ''

    const [search, setSearch] = useState('')
    const [order, setOrder] = useState<DataScopeOrder>(PROJECT_DESC_ORDER)
    const [activityTicket, setActivityTicket] = useState<DataItem>()

    const {
      isOpen: isCreateActivityOpen,
      openModal: openCreateActivityDrawer,
      closeModal: closeCreateActivityDrawer,
    } = useModalHandler()

    const {
      drawerItem,
      setDrawerItem,
      isDrawerOpen,
      openDrawer,
      closeDrawer,
      startInEditMode,
    } = useDataItemDrawer()

    const onSearchChange: ChangeEventHandler<HTMLInputElement> = (event) => {
      setSearch(event.target.value)
    }

    const orderOptions: Option[] = [
      { label: t('common.sort.recentFirst'), value: 'desc' },
      { label: t('common.sort.recentLast'), value: 'asc' },
    ]
    const currentOrderValue = isEqual(order, PROJECT_DESC_ORDER)
      ? 'desc'
      : 'asc'
    const selectedOrderOption = orderOptions.find(
      (o) => o.value === currentOrderValue,
    )
    const handleOrderChange = (newValue: SingleValue<Option | unknown>) => {
      const val = (newValue as Option)?.value
      setOrder(val === 'asc' ? PROJECT_ASC_ORDER : PROJECT_DESC_ORDER)
    }

    const { items, hasMore, loadMore, count } = useTicketTabData({
      subjectId,
      search,
      order,
      listSize: LIST_SIZE,
    })

    const onActivityCreated = async (newActivity: DataItem) => {
      if (!activityTicket) return

      try {
        await ActivityProjectRelation.create({
          project: activityTicket.id(),
          activity: newActivity.id(),
        })
        showInfoBoxToast({
          type: 'success',
          iconPosition: 'top',
          text: t('components.activity.modal.success'),
          actionLabel: t('components.activity.goToActivity'),
          onAction: () => navigateTo(newActivity),
        })
      } catch (error) {
        errorToast(error as Error)
        reportError(error)
      } finally {
        closeCreateActivityDrawer()
      }
    }

    if (!subjectId) return

    const header = (
      <div className="tab-header-wrapper">
        <div className="search-filter">
          <Search
            name={searchInputName}
            onChange={onSearchChange}
            searchWrapperClassName="tab-header-search"
            placeholder={t('search.title')}
          />
          <StringText value={t('common.sortBy')} />
          <SimpleSelect
            name="order-select"
            className="small"
            options={orderOptions}
            value={selectedOrderOption}
            onChange={handleOrderChange}
          />
        </div>
      </div>
    )

    return (
      <>
        <Sublist>
          <ScrollablePanel header={header}>
            <div className="sublist-wrapper">
              {items.map((item) => (
                <TicketSublistItem
                  key={item.id}
                  ticket={item.ticket}
                  onOpenDrawer={(ticket, startInEditMode = false) => {
                    setDrawerItem(ticket)
                    openDrawer(startInEditMode)
                  }}
                  onCreateActivity={(ticket) => {
                    setActivityTicket(ticket)
                    openCreateActivityDrawer()
                  }}
                />
              ))}
              <SublistFooter
                amount={items.length}
                total={count}
                labelKey={getEntityLabelKey(DataClassName.TICKET)}
                onLoadMore={hasMore ? loadMore : undefined}
              />
            </div>
          </ScrollablePanel>
        </Sublist>
        {drawerItem && (
          <DataItemDrawer
            key={drawerItem.id()}
            isOpen={isDrawerOpen}
            close={closeDrawer}
            dataItem={drawerItem}
            startInEditMode={startInEditMode}
          />
        )}
        <DataClassActivityDrawer
          isOpen={isCreateActivityOpen}
          close={closeCreateActivityDrawer}
          onCreated={onActivityCreated}
          linkedEntityName={getDataItemTitle(activityTicket)}
        />
      </>
    )
  },
)
